import { Input } from '@monorepo/components'
import { Field, Formik, setNestedObjectValues } from 'formik'
import Select, { MultiValue, SingleValue } from 'react-select'
import React, { ChangeEvent, useState } from 'react'
import { cloneDeep } from 'lodash'
import * as S from './styles'
import {
  Checkbox,
  FormControlLabel,
  Box,
  Typography,
  IconButton,
} from '@mui/material'
import DeleteForeverIcon from '@mui/icons-material/DeleteForever'
import { pink } from '@mui/material/colors'
import {
  DealerHelper,
  isNumeric,
  ChannelHelper,
  isNumber,
  isObject,
  emptyReactSelectOption,
  CoopAllowanceCapabilityType,
  isNonEmptyString,
  createReactSelectOptions,
  getReactSelectOption,
  MasterDealerAssociation,
  gatherReactSelectOptions,
  isNonEmptyArray,
} from '@monorepo/infra'
import {
  CoopAllowance,
  RegistrationUserResponse,
  IReactSelectOption,
  IAdditionalReactSelectOptions,
  IDealerForm,
  IDealerFormInitialValuesParsed,
} from '@monorepo/interfaces'
import { colors } from '@monorepo/theme'

type DealerFormProps = {
  currentUserProfile?: RegistrationUserResponse
  additionalMasterDealerIdsOptions?: IAdditionalReactSelectOptions
  additionalChannels?: IAdditionalReactSelectOptions
  close: () => void
  actionForm: (data: IDealerForm) => void
  initialValues?: IDealerFormInitialValuesParsed
  error: string
}

const coopAllowanceCapabilityOptions =
  DealerHelper.getCoopAllowanceCapabilityOptions()

const sxFormControlLabel = {
  color: pink[800],
  '& .MuiFormControlLabel-label': { fontWeight: 600 },
}

const sxCheckbox = {
  color: pink[800],
  '&.Mui-checked': { color: pink[600] },
  '& .MuiSvgIcon-root': { fontSize: 28 },
}

const dealerGroupsMemberOnly = [
  { value: '/dealer', label: 'Member' }
]

const DealerForm: React.FC<DealerFormProps> = ({
  initialValues = {
    firstName: '',
    lastName: '',
    email: '',
    channels: null,
    types: null,
    status: dealerGroupsMemberOnly[0],
    hasCoop: false,
    userId: '',
    dealerId: '',
    coopAllowances: undefined,
    uniqueDealerId: undefined,
    businessName: undefined,
  },
  currentUserProfile,
  additionalMasterDealerIdsOptions,
  additionalChannels,
  actionForm,
  close,
  error,
}) => {
  const [hasCoopAllowance, setHasCoopAllowance] = useState(false)
  const [currentCoopAllowance, setCurrentCoopAllowance] = useState<
    CoopAllowance | undefined
  >(undefined)

  const masterDealerIdsOptions = gatherReactSelectOptions(
    createReactSelectOptions(
      isObject(currentUserProfile)
        ? ChannelHelper.getMasterDealerIds(
            currentUserProfile!.dealer.associatedMasterDealers
          )
        : undefined
    ),
    additionalMasterDealerIdsOptions
  )

  const [masterDealerId, setMasterDealerId] = useState<string | undefined>(
    isNonEmptyArray(masterDealerIdsOptions)
      ? (masterDealerIdsOptions as IReactSelectOption[])[0].value
      : undefined
  )

  // Helper function to get channels for a specific master dealer ID
  // Includes hardcoded mappings for special DIRECTV accounts
  const getChannelsForMasterDealer = (
    masterDealerId?: string
  ): string[] => {
    if (!isNonEmptyString(masterDealerId)) {
      return []
    }

    // Hardcoded channel mappings for special DIRECTV accounts
    switch (masterDealerId) {
      case DealerHelper.masterDealerIdDirectvEmployees: // 9999999999
        return ['LSP', 'MDU', 'D2D', 'EM', 'NR', 'ISP']

      case DealerHelper.masterDealerIdDirectvISPDealers: // 7777777777
        return ['ISP']

      case DealerHelper.masterDealerIdDirectvNDDealers: // 8888888888
        return ['NR']

      default:
        // For regular master dealers, use existing logic from current user profile
        return ChannelHelper.getChannels(
          (isObject(currentUserProfile)
            ? currentUserProfile!.dealer.associatedMasterDealers
            : undefined) as MasterDealerAssociation[],
          masterDealerId
        )
    }
  }

  const [availableChannelOptions, setAvailableChannelOptions] = useState<
    IReactSelectOption[] | undefined
  >(
    gatherReactSelectOptions(
      createReactSelectOptions(
        getChannelsForMasterDealer(
          isNonEmptyArray(masterDealerIdsOptions)
            ? (masterDealerIdsOptions as IReactSelectOption[])[0].value
            : undefined
        )
      ),
      additionalChannels
    )
  )

  const canUseCoop = currentUserProfile?.dealer?.hasCoop === true

  const handleChannelsChange = (
    newValue: MultiValue<IReactSelectOption>,
    values: IDealerFormInitialValuesParsed,
    setFieldValue?: (
      field: string,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      value: any,
      shouldValidate?: boolean | undefined
    ) => void
  ) => {
    if (
      !newValue ||
      !Array.isArray(newValue) ||
      !newValue.length ||
      !Array.isArray(values.coopAllowances)
    ) {
      if (values.coopAllowances) {
        setFieldValue?.('coopAllowances', undefined)
      }

      if (currentCoopAllowance) {
        setCurrentCoopAllowance(undefined)
      }

      return
    }

    const coopAllowances = cloneDeep(values.coopAllowances)

    for (let i = coopAllowances.length - 1; i >= 0; --i) {
      const masterDealer = ChannelHelper.splitMasterDealerId(
        coopAllowances[i].masterDealerId
      )

      if (
        !masterDealer?.channel ||
        !newValue.find(
          (option) =>
            option.value.toLowerCase() === masterDealer.channel.toLowerCase()
        )
      ) {
        coopAllowances.splice(i, 1)
      }
    }

    setFieldValue?.(
      'coopAllowances',
      coopAllowances.length ? coopAllowances : undefined
    )

    if (currentCoopAllowance) {
      if (coopAllowances.length) {
        if (
          !coopAllowances.find(
            (item) =>
              item.masterDealerId === currentCoopAllowance.masterDealerId
          )
        ) {
          setCurrentCoopAllowance(coopAllowances[0])
        }
      } else {
        setCurrentCoopAllowance(undefined)
      }
    }
  }

  const getCurrentCoopAllowanceChannelOption = () => {
    const masterDealer = ChannelHelper.splitMasterDealerId(
      currentCoopAllowance?.masterDealerId
    )

    return !!masterDealer?.channel
      ? ChannelHelper.getChannelOptions().find(
          (item) =>
            item.value.toLowerCase() === masterDealer.channel.toLowerCase()
        )
      : emptyReactSelectOption
  }

  const getCurrentCoopAllowanceCapability = () =>
    isNumber(currentCoopAllowance?.capability)
      ? DealerHelper.getCoopAllowanceCapabilityOptions().find(
          (item) => item.value === currentCoopAllowance?.capability?.toString()
        )
      : emptyReactSelectOption

  const handleHasCoopChange = (
    newValue: boolean,
    setFieldValue?: (
      field: string,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      value: any,
      shouldValidate?: boolean | undefined
    ) => void
  ) => {
    setFieldValue?.('hasCoop', newValue)

    if (!newValue) {
      return
    }

    setHasCoopAllowance(false)
    setFieldValue?.('coopAllowances', undefined)
    setCurrentCoopAllowance(undefined)
  }

  const handleHasCoopAllowanceChange = (
    newValue: boolean,
    setFieldValue?: (
      field: string,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      value: any,
      shouldValidate?: boolean | undefined
    ) => void
  ) => {
    setHasCoopAllowance(newValue)

    if (newValue) {
      setFieldValue?.('hasCoop', false)
    } else {
      setFieldValue?.('coopAllowances', undefined)
      setCurrentCoopAllowance(undefined)
    }
  }

  const handleCoopAllowanceChannelChange = (
    newValue: MultiValue<IReactSelectOption> | SingleValue<IReactSelectOption>,
    values: IDealerFormInitialValuesParsed,
    setFieldValue?: (
      field: string,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      value: any,
      shouldValidate?: boolean | undefined
    ) => void
  ) => {
    if (!isObject(newValue)) {
      return
    }

    const _masterDealerId = ChannelHelper.getMasterDealerId(
      masterDealerId as string,
      (
        (newValue as SingleValue<IReactSelectOption>)?.value as string
      )?.toUpperCase()
    )

    const coopAllowances =
      Array.isArray(values.coopAllowances) && values.coopAllowances.length
        ? cloneDeep(values.coopAllowances)
        : []

    let coopAllowance = DealerHelper.getCoopAlloance(
      coopAllowances,
      _masterDealerId
    )

    if (!coopAllowance) {
      coopAllowance = {
        masterDealerId: _masterDealerId,
        allowance: undefined,
        capability: undefined,
      }

      coopAllowances.push(coopAllowance)
      setFieldValue?.('coopAllowances', coopAllowances)
    }

    setCurrentCoopAllowance(coopAllowance)
  }

  const handleCoopAllowanceChange = (
    newValue: string,
    values: IDealerFormInitialValuesParsed,
    setFieldValue?: (
      field: string,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      value: any,
      shouldValidate?: boolean | undefined
    ) => void
  ) => {
    const coopAllowances = cloneDeep(values.coopAllowances)

    let coopAllowance = DealerHelper.getCoopAlloance(
      coopAllowances,
      currentCoopAllowance?.masterDealerId
    )

    if (isNumeric(newValue)) {
      const _newValue = parseFloat(newValue)

      if (coopAllowance) {
        if (
          coopAllowance.capability ===
          CoopAllowanceCapabilityType.SINGLE_ALLOCATION
        ) {
          coopAllowance.allowance = _newValue
          delete coopAllowance.recurringAllowance
        }

        if (
          coopAllowance.capability ===
          CoopAllowanceCapabilityType.MONTHLY_RECURRING
        ) {
          coopAllowance.recurringAllowance = _newValue
          delete coopAllowance.allowance
        }
      } else {
        coopAllowance = {
          masterDealerId:
            currentCoopAllowance?.masterDealerId?.toUpperCase() as string,
          ...(currentCoopAllowance?.capability ===
            CoopAllowanceCapabilityType.SINGLE_ALLOCATION && {
            allowance: _newValue,
          }),
          capability: currentCoopAllowance?.capability,
          ...(currentCoopAllowance?.capability ===
            CoopAllowanceCapabilityType.MONTHLY_RECURRING && {
            recurringAllowance: _newValue,
          }),
        }
      }

      setCurrentCoopAllowance(coopAllowance)
    } else {
      if (coopAllowance) {
        coopAllowance.allowance = undefined
        coopAllowance.recurringAllowance = undefined
      }

      setCurrentCoopAllowance(coopAllowance)
    }

    setFieldValue?.('coopAllowances', coopAllowances)
  }

  const handleCoopAllowanceCapabilityChange = (
    newValue: MultiValue<IReactSelectOption> | SingleValue<IReactSelectOption>,
    values: IDealerFormInitialValuesParsed,
    setFieldValue?: (
      field: string,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      value: any,
      shouldValidate?: boolean | undefined
    ) => void
  ) => {
    const coopAllowances = cloneDeep(values.coopAllowances)

    let coopAllowance = DealerHelper.getCoopAlloance(
      coopAllowances,
      currentCoopAllowance?.masterDealerId
    )

    if (isObject(newValue)) {
      const _newValue = parseInt(
        (newValue as SingleValue<IReactSelectOption>)?.value as string,
        10
      )

      if (coopAllowance) {
        coopAllowance.capability = _newValue

        if (_newValue === CoopAllowanceCapabilityType.SINGLE_ALLOCATION) {
          delete coopAllowance.recurringAllowance
        }

        if (_newValue === CoopAllowanceCapabilityType.MONTHLY_RECURRING) {
          delete coopAllowance.allowance
        }
      } else {
        coopAllowance = {
          masterDealerId:
            currentCoopAllowance?.masterDealerId?.toUpperCase() as string,
          ...(_newValue === CoopAllowanceCapabilityType.SINGLE_ALLOCATION && {
            allowance: currentCoopAllowance?.allowance,
          }),
          capability: _newValue,
          ...(_newValue === CoopAllowanceCapabilityType.MONTHLY_RECURRING && {
            recurringAllowance: currentCoopAllowance?.recurringAllowance,
          }),
        }
      }

      setCurrentCoopAllowance(coopAllowance)
    } else {
      if (coopAllowance) {
        coopAllowance.allowance = undefined
        coopAllowance.capability = undefined
      }

      setCurrentCoopAllowance(coopAllowance)
    }

    setFieldValue?.('coopAllowances', coopAllowances)
  }

  const handleDeleteCoopAllowance = (
    values: IDealerFormInitialValuesParsed,
    setFieldValue?: (
      field: string,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      value: any,
      shouldValidate?: boolean | undefined
    ) => void
  ) => {
    if (
      !currentCoopAllowance ||
      !Array.isArray(values.coopAllowances) ||
      !values.coopAllowances.length
    ) {
      return
    }

    const coopAllowances = DealerHelper.deleteCoopAlloance(
      cloneDeep(values.coopAllowances),
      currentCoopAllowance.masterDealerId
    )

    setFieldValue?.('coopAllowances', coopAllowances)

    setCurrentCoopAllowance(
      coopAllowances?.length ? coopAllowances[0] : undefined
    )
  }

  const handleMasterDealerIdChange = (
    newValue: MultiValue<IReactSelectOption> | SingleValue<IReactSelectOption>,
    values: IDealerFormInitialValuesParsed,
    setFieldValue?: (
      field: string,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      value: any,
      shouldValidate?: boolean | undefined
    ) => void
  ) => {
    if (!isObject(newValue)) {
      return
    }

    const masterDealerId = (newValue as SingleValue<IReactSelectOption>)?.value
    if (!isNonEmptyString(masterDealerId)) {
      return
    }

    setMasterDealerId(masterDealerId)

    setAvailableChannelOptions(
      gatherReactSelectOptions(
        createReactSelectOptions(
          getChannelsForMasterDealer(masterDealerId)
        ),
        additionalChannels
      )
    )

    setFieldValue?.('hasCoop', false)
    handleHasCoopChange(false, setFieldValue)
    handleHasCoopAllowanceChange(false, setFieldValue)

    setFieldValue?.('channels', [])
    handleChannelsChange(
      undefined as unknown as IReactSelectOption[],
      values,
      setFieldValue
    )
  }

  return (
    <>
      {isNonEmptyString(error) && (
        <S.ErrorMessage>{error.trim()}</S.ErrorMessage>
      )}
      <Formik
        initialValues={initialValues}
        validationSchema={DealerHelper.TeamMemberSchema}
        validateOnBlur={true}
        validateOnChange={true}
        onSubmit={(values, actions) => {
          actions.setSubmitting(false)
          const valuesParsed = {
            ...values,
            email: values?.email?.trim()?.toLowerCase(),
            channels: values?.channels?.map((data) => data.value) || [],
            dealerTypes: values?.types?.map((data) => data.value) || [],
            groups: [values?.status?.value],
            masterDealerId,
          }
          actionForm(valuesParsed)
        }}
      >
        {({
          errors,
          touched,
          values,
          setFieldValue,
          setFieldTouched,
          validateForm,
          submitForm,
          setTouched,
        }) => (
          <S.DialogForm>
            <Select
              name="masterDealerId"
              placeholder="Master dealer ID"
              options={masterDealerIdsOptions}
              isSearchable={false}
              components={{
                IndicatorSeparator: () => null,
              }}
              value={getReactSelectOption(
                masterDealerIdsOptions,
                masterDealerId
              )}
              onChange={(newValue) =>
                handleMasterDealerIdChange(newValue, values, setFieldValue)
              }
              styles={S.SelectStyle}
            />

            <Field
              id="uniqueDealerId"
              name="uniqueDealerId"
              component={Input}
              value={values.uniqueDealerId}
              invalid={touched.uniqueDealerId && errors.uniqueDealerId}
              invalidMessage={errors.uniqueDealerId}
              label="Unique dealer ID"
              onBlur={() => setFieldTouched('uniqueDealerId')}
              onChange={(option: { target: { value: string } }) => {
                setFieldValue('uniqueDealerId', option.target.value)
              }}
            />

            <Field
              id="businessName"
              name="businessName"
              component={Input}
              value={values.businessName}
              invalid={touched.businessName && errors.businessName}
              invalidMessage={errors.businessName}
              label="Business name"
              onBlur={() => setFieldTouched('businessName')}
              onChange={(option: { target: { value: string } }) => {
                setFieldValue('businessName', option.target.value)
              }}
            />

            <Field
              id="firstName"
              name="firstName"
              component={Input}
              value={values.firstName}
              invalid={touched.firstName && errors.firstName}
              invalidMessage={errors.firstName}
              label="First name"
              onBlur={() => setFieldTouched('firstName')}
              onChange={(option: { target: { value: string } }) => {
                setFieldValue('firstName', option.target.value)
              }}
            />

            <Field
              id="lastName"
              name="lastName"
              component={Input}
              value={values.lastName}
              invalid={touched.lastName && errors.lastName}
              invalidMessage={errors.lastName}
              label="Last name"
              onBlur={() => {
                setFieldTouched('lastName')
              }}
              onChange={(option: { target: { value: string } }) => {
                setFieldValue('lastName', option.target.value)
              }}
            />

            <Field
              id="email"
              name="email"
              component={Input}
              value={values.email}
              invalid={touched.email && errors.email}
              invalidMessage={errors.email}
              label="Email Address"
              onBlur={() => {
                setFieldTouched('email')
              }}
              onChange={(option: { target: { value: string } }) => {
                setFieldValue('email', option.target.value)
              }}
            />
            <Select
              name="channels"
              placeholder="Channel(s)"
              isMulti
              options={availableChannelOptions}
              value={values.channels}
              onChange={(newValue) => {
                setFieldValue('channels', newValue)
                handleChannelsChange(newValue, values, setFieldValue)
              }}
              onBlur={() => setFieldTouched('channels')}
              components={{
                IndicatorSeparator: () => null,
              }}
              isClearable={true}
              styles={S.MultiSelectStyle}
            />

            <Select
              name="status"
              placeholder="Status"
              options={dealerGroupsMemberOnly}
              components={{
                IndicatorSeparator: () => null,
              }}
              isSearchable={false}
              value={values.status || dealerGroupsMemberOnly[0]}
              onChange={(newValue) => setFieldValue('status', newValue)}
              styles={S.MultiSelectStyle}
            />

            {/* {canUseCoop ? (
              <>
                <S.CheckboxCoopContainer>
                  <FormControlLabel
                    label="Unlimited Co-Op Access"
                    sx={sxFormControlLabel}
                    control={
                      <Checkbox
                        id="hasCoop"
                        name="hasCoop"
                        checked={values.hasCoop}
                        onChange={(_, checked) =>
                          handleHasCoopChange(checked, setFieldValue)
                        }
                        sx={sxCheckbox}
                        disabled={!canUseCoop}
                      />
                    }
                  />
                  <FormControlLabel
                    label="Custom Co-Op Allowance"
                    sx={sxFormControlLabel}
                    control={
                      <Checkbox
                        id="hasCoopAllowance"
                        name="hasCoopAllowance"
                        checked={hasCoopAllowance}
                        onChange={(_, checked) =>
                          handleHasCoopAllowanceChange(checked, setFieldValue)
                        }
                        sx={sxCheckbox}
                        disabled={!canUseCoop}
                      />
                    }
                  />
                </S.CheckboxCoopContainer>
                <Box
                  component="fieldset"
                  sx={{
                    display: hasCoopAllowance ? 'grid' : 'none',
                    gridTemplateColumns: '1fr 1fr 1fr 50px',
                    gap: '10px 20px',
                    borderColor: colors.mediumGrey,
                    borderStyle: 'solid',
                    borderWidth: 1,
                    padding: '16px',
                  }}
                >
                  <Typography
                    component="legend"
                    sx={{
                      padding: '0 5px',
                      fontFamily: 'PFDINText',
                      fontWeight: 600,
                      fontSize: '15px',
                      color: colors.mediumGrey,
                    }}
                  >
                    Co-Op allowances
                  </Typography>

                  <Select
                    name="coopAllowanceChannel"
                    placeholder="Channel"
                    options={values.channels as IReactSelectOption[]}
                    components={{
                      IndicatorSeparator: () => null,
                    }}
                    value={getCurrentCoopAllowanceChannelOption()}
                    onChange={(newValue) =>
                      handleCoopAllowanceChannelChange(
                        newValue,
                        values,
                        setFieldValue
                      )
                    }
                    styles={S.SelectStyle}
                  />

                  <Select
                    name="coopAllowanceCapability"
                    placeholder="Co-Op allowance capability"
                    options={coopAllowanceCapabilityOptions}
                    components={{
                      IndicatorSeparator: () => null,
                    }}
                    value={getCurrentCoopAllowanceCapability()}
                    onChange={(newValue) =>
                      handleCoopAllowanceCapabilityChange(
                        newValue,
                        values,
                        setFieldValue
                      )
                    }
                    styles={S.SelectStyle}
                  />

                  <Field
                    id="coopAllowance"
                    name="coopAllowance"
                    component={Input}
                    value={
                      currentCoopAllowance?.capability ===
                      CoopAllowanceCapabilityType.MONTHLY_RECURRING
                        ? currentCoopAllowance?.recurringAllowance || ''
                        : currentCoopAllowance?.allowance || ''
                    }
                    invalid={touched.coopAllowances && errors.coopAllowances}
                    invalidMessage={errors.coopAllowances}
                    label="Co-Op allowance"
                    onBlur={() => {
                      setFieldTouched('coopAllowance')
                    }}
                    onChange={(e: ChangeEvent<HTMLInputElement>) =>
                      handleCoopAllowanceChange(
                        e.target.value,
                        values,
                        setFieldValue
                      )
                    }
                  />

                  <S.DeleteCoopAllowanceButtonContainer>
                    <IconButton
                      aria-label="deletecoopallowance"
                      title="Delete"
                      disabled={!currentCoopAllowance}
                      onClick={() =>
                        handleDeleteCoopAllowance(values, setFieldValue)
                      }
                    >
                      <DeleteForeverIcon />
                    </IconButton>
                  </S.DeleteCoopAllowanceButtonContainer>
                </Box>
              </>
            ) : undefined} */}

            <S.DialogActions>
              <S.ModalButton
                colorOption="stroke"
                label="CANCEL"
                onClick={() => close()}
              />
              <S.ModalButton
                colorOption="black"
                label="SAVE"
                onClick={() => {
                  validateForm().then((errors) => {
                    setTouched(setNestedObjectValues(errors, true))
                  })
                  submitForm()
                }}
              />
            </S.DialogActions>
          </S.DialogForm>
        )}
      </Formik>
    </>
  )
}

export default DealerForm
